/**
 * SgpFlowScreenView — presentational render for SgpFlowScreen.
 * Extracted verbatim from screens/SgpFlowScreen.jsx (god-file decomposition).
 * Pure view: receives all state/handlers/derived values as props. Preserves the
 * original early-return structure (isInitializing / loading / etc. guards return
 * splash screens before the main render). Completeness machine-verified by
 * scripts/check-extraction.js; the call site is generated from the SAME prop list.
 */

import { View, Text, TouchableOpacity, Platform, Animated, Image, Pressable } from 'react-native';
import { BlurView } from 'expo-blur';
import { Ionicons } from '@expo/vector-icons';
import NavigationHeader from './NavigationHeader';
import PressableScale from './motion/PressableScale';
import { ScreenBackground } from './ThemeGradients';
import GradientButton from './GradientButton';
import BetSlipModal from './BetSlipModal';
import BetSlipSubmissionScreen from './BetSlipSubmissionScreen';
import QueryResolutionDisplay from './QueryResolutionDisplay';
import SavedRequestsModal from './SavedRequestsModal';
import QueryLimitModal from './QueryLimitModal';
import SearchInput from './SearchInput';
import SgpResultsRenderer from './SgpResultsRenderer';
import SearchLoadingView from './SearchLoadingView';
import { useRevealWindow } from '../hooks/useRevealWindow';
import ShareBetSlipModal from './ShareBetSlipModal';
import SgpFilterPanel from './SgpFilterPanel';
import BookSelectorDropdown from './BookSelectorDropdown';
import PromotionBanner from './PromotionBanner';
import { calculateSgpMath } from '../utils/sgpFlowUtils';
import { describeSearchEmpty } from '../utils/newResultsEmptyState';
import { query } from 'firebase/firestore';
import { styles } from '../screens/sgpFlowStyles';


export default function SgpFlowScreenView({ IS_MOBILE, MAX_LEGS, cardReferenceTime, _allBooksCount, _effectiveBooksSort, _getMyBooksBest, _isAdminTop, _myBooksCount, _myBooksList, _relaxMinBooksFromBg, _sortEvForBook, _userPickedSortRef, _wantsMyBooksMode, alreadySentToBackgroundRef, availableLegCounts, availableMarkets, availablePlayers, availableSportsbooks, avgMs, autoBroadened, betMode, bookOverride, builderStake, canAddBackgroundQuery, canShowDevResultsCopy, cancelLoading, clarification, setClarification, clearAllFilters, computeCap, consensusOptions, copyResultsFlash, currentPage, debugPanelExpanded, detectedPromotion, didBroaden, disableMinBooksFilter, disableMyBooksPriority, errorMsg, fetchedOptions, filterDeepLinksOnly, filterLegCount, filterLineType, filterMarkets, filterPlayers, filterPositiveEvOnly, filterResultType, filterSportsbooks, filteredAndSortedOptions, fixturesFoundNoOdds, getFixtureLogosFromTicker, getMarketPeers, goBack, handleAddFetchedLeg, handleApplySavedRequest, handleBetSlipSubmission, handleCopyAllResults, handleCopyShareUrl, handleQueryFeedback, handleSaveBetSlip, handleSaveQuery, handleSaveRequest, handleShareBetSlip, handleSubmissionComplete, handleUnwatchFixture, handleWatchFixture, hasPropsResults, hasStartedSearch, hasTeamResults, isInitializing, isLiveQuery, isSgpResults, itemsPerPage, lastNotice, loading, navigateTo, noResults, openDeepLink, pageOpacity, pageTranslateY, pendingQuery, playerChipHeadshots, preferredSportsbook, progress, progressText, queryFeedback, queryUsage, requestBroaden, requestedDirection, requestedLine, resolveClarification, responseMetadata, responsive, resultsListForCopyRef, retryFetch, routePayload, searchStatus, searchText, searchedSportsbooks, selectedLegs, selectedOptions, sendToBackground, setBookOverride, setBooksViewOverride, setCurrentPage, setDebugPanelExpanded, setDetectedPromotion, setDidBroaden, setDisableMinBooksFilter, setFilterDeepLinksOnly, setFilterLegCount, setFilterLineType, setFilterMarkets, setFilterPlayers, setFilterPositiveEvOnly, setFilterResultType, setFilterSportsbooks, setPreferredSportsbook, setRelaxMinBooksFromBg, setSearchText, setShowAllMathOverride, setShowBetSlipModal, setShowBookSelector, setShowQueryLimitModal, setShowSavedRequests, setShowShareModal, setSortBy, setSortMethod, sgpGenerationStats, shareUrl, shouldUseNewResultsUI, showAllMathOverride, showBetSlipModal, showBookSelector, showQueryLimitModal, showSavedRequests, showShareModal, showSubmissionScreen, sortBy, sortMethod, toggleMarketFilter, togglePlayerFilter, toggleSportsbookFilter, totalHeaderHeight, undoAutoBroaden, userProfile, watchedFixtures }) {
    // Cards that mount right after a search finishes morph out of their placeholders.
    const revealActive = useRevealWindow(loading || isInitializing);
    const searchEmpty = describeSearchEmpty({
        fetchedCount: fetchedOptions?.length || 0,
        resultType: responseMetadata?._resultType,
        message: responseMetadata?._message,
        freezeMode: !!responseMetadata?.freeze_mode,
        failureKind: responseMetadata?._failureKind,
    });
    if (isInitializing) {
        return (
            <View style={styles.container}>
                <View style={styles.background} />
                <View style={[styles.splashContainer, { 
                    paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
                }]}>
                    <NavigationHeader />
                    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                        <Image source={require('../assets/icon.png')} style={{ width: 56, height: 56, borderRadius: 12, opacity: 0.7 }} resizeMode="contain" />
                    </View>
                </View>
            </View>
        );
    }

    // Loading screen: status panel + shimmering placeholders on the results frame.
    if (loading) {
        return (
            <SearchLoadingView
                IS_MOBILE={IS_MOBILE}
                alreadySentToBackgroundRef={alreadySentToBackgroundRef}
                avgMs={avgMs}
                betMode={betMode}
                canAddBackgroundQuery={canAddBackgroundQuery}
                cancelLoading={cancelLoading}
                computeCap={computeCap}
                errorMsg={errorMsg}
                goBack={goBack}
                isLiveQuery={isLiveQuery}
                pendingQuery={pendingQuery}
                progress={progress}
                progressText={progressText}
                responsive={responsive}
                retryFetch={retryFetch}
                routePayload={routePayload}
                searchStatus={searchStatus}
                selectedLegs={selectedLegs}
                sendToBackground={sendToBackground}
                setShowBetSlipModal={setShowBetSlipModal}
                totalHeaderHeight={totalHeaderHeight}
            />
        );
    }

    return (
        <View style={styles.container}>
            <ScreenBackground />
            <View style={[styles.splashContainer, { 
                paddingTop: Platform.OS === 'web' ? (totalHeaderHeight + responsive(12, 20, 32)) : responsive(12, 20, 16) 
            }]}>
                <NavigationHeader />

                {/* Search Bar for New Queries — same animated component as the Home page */}
                <SearchInput
                    onNavigate={navigateTo}
                />

                <Animated.View 
                    style={[
                        styles.mainContent,
                        {
                            opacity: pageOpacity,
                            transform: [
                                { translateY: pageTranslateY }
                            ]
                        }
                    ]}
                >
                    <BlurView tint="dark" style={styles.blurContent}>

                        {!!lastNotice && (
                          <Text style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>{lastNotice}</Text>
                        )}

                        {/* Book Override Selector */}
                        <View style={{ alignSelf: 'stretch', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14 }}>Books:</Text>
                            <PressableScale onPress={() => setShowBookSelector(!showBookSelector)} style={[styles.bookOverrideBtn, bookOverride ? styles.bookOverrideBtnActive : null]} hoverStyle={styles.bookOverrideBtnHovered}>
<Text style={[styles.bookOverrideBtnText, bookOverride ? styles.bookOverrideBtnTextActive : null]}>
                                    {bookOverride || 'All Books'}
                                  </Text>
</PressableScale>
                            {bookOverride && (
                              <PressableScale onPress={() => setBookOverride(null)} style={[styles.clearOverrideBtn]} hoverStyle={styles.clearOverrideBtnHovered}>
<Text style={styles.clearOverrideBtnText}>×</Text>
</PressableScale>
                            )}
                          </View>
                          <PressableScale onPress={() => handleSaveQuery(pendingQuery)} style={[styles.savedRequestsBtn]} hoverStyle={styles.savedRequestsBtnHovered}>
<Text style={styles.savedRequestsBtnText}>💾 Save Query</Text>
</PressableScale>
                        </View>

                        {/* Book Selector Dropdown */}
                        {showBookSelector && (
                          <BookSelectorDropdown
                            bookOverride={bookOverride}
                            onSelect={setBookOverride}
                            onClose={() => setShowBookSelector(false)}
                          />
                        )}
                        
                        {/* Promotion Detected Banner */}
                        {detectedPromotion && detectedPromotion.type && (
                          <PromotionBanner
                            promotion={detectedPromotion}
                            onDismiss={() => setDetectedPromotion(null)}
                          />
                        )}
                        
                        {/* Search & Filter Panel — only in classic UI */}
                        {!shouldUseNewResultsUI && fetchedOptions.length > 0 && (
                          <SgpFilterPanel
                            _isAdminTop={_isAdminTop}
                            _userPickedSortRef={_userPickedSortRef}
                            availableLegCounts={availableLegCounts}
                            availableMarkets={availableMarkets}
                            availablePlayers={availablePlayers}
                            availableSportsbooks={availableSportsbooks}
                            canShowDevResultsCopy={canShowDevResultsCopy}
                            clearAllFilters={clearAllFilters}
                            copyResultsFlash={copyResultsFlash}
                            debugPanelExpanded={debugPanelExpanded}
                            fetchedOptions={fetchedOptions}
                            filterDeepLinksOnly={filterDeepLinksOnly}
                            filterLegCount={filterLegCount}
                            filterLineType={filterLineType}
                            filterMarkets={filterMarkets}
                            filterPlayers={filterPlayers}
                            filterPositiveEvOnly={filterPositiveEvOnly}
                            filterResultType={filterResultType}
                            filterSportsbooks={filterSportsbooks}
                            filteredAndSortedOptions={filteredAndSortedOptions}
                            handleCopyAllResults={handleCopyAllResults}
                            handleQueryFeedback={handleQueryFeedback}
                            hasPropsResults={hasPropsResults}
                            hasTeamResults={hasTeamResults}
                            isLiveQuery={isLiveQuery}
                            isSgpResults={isSgpResults}
                            playerChipHeadshots={playerChipHeadshots}
                            queryFeedback={queryFeedback}
                            responseMetadata={responseMetadata}
                            searchText={searchText}
                            searchedSportsbooks={searchedSportsbooks}
                            setCurrentPage={setCurrentPage}
                            setDebugPanelExpanded={setDebugPanelExpanded}
                            setFilterDeepLinksOnly={setFilterDeepLinksOnly}
                            setFilterLegCount={setFilterLegCount}
                            setFilterLineType={setFilterLineType}
                            setFilterMarkets={setFilterMarkets}
                            setFilterPlayers={setFilterPlayers}
                            setFilterPositiveEvOnly={setFilterPositiveEvOnly}
                            setFilterResultType={setFilterResultType}
                            setFilterSportsbooks={setFilterSportsbooks}
                            setSearchText={setSearchText}
                            setSortBy={setSortBy}
                            setSortMethod={setSortMethod}
                            sortBy={sortBy}
                            sortMethod={sortMethod}
                            toggleMarketFilter={toggleMarketFilter}
                            togglePlayerFilter={togglePlayerFilter}
                            toggleSportsbookFilter={toggleSportsbookFilter}
                            userProfile={userProfile}
                          />
                        )}

                        {/* Guard: if no results yet and we aren't loading, show prompt to search */}
                        {!loading && !fetchedOptions.length && !noResults && !hasStartedSearch ? (
                        <View style={styles.betSlipSection}>
                        <View style={styles.betSlipHeader}>
                            <View style={styles.betSlipTitleContainer}>
                                <View style={styles.betSlipTitle}>
                                  <Text style={styles.betSlipTitleText}>Ready to search</Text>
                                  <Text style={styles.betSlipSubtitle}>Enter a query above to find betting odds.</Text>
                                </View>
                              </View>
                            </View>
                          </View>
                        ) : null}


                        {/* ⭐ Active Filters Banner — single context-aware pill (classic UI only).
                            All Books mode: "Showing 5+ sportsbooks"
                            My Books mode:  "Showing 3+ My Books"
                            Same Show All / Restore toggle in both cases. */}
                        {!shouldUseNewResultsUI && fetchedOptions.length > 0 && (
                          _wantsMyBooksMode
                            ? _myBooksList.length > 0   // always show in My Books mode
                            : (userProfile?.preferences?.minBooksStraights || 1) > 1
                        ) && (() => {
                          const _mbMode = _wantsMyBooksMode && _myBooksList.length > 0;
                          const _minVal = _mbMode
                            ? (userProfile?.preferences?.minBooksMyBooks ?? 3)
                            : (userProfile?.preferences?.minBooksStraights || 5);
                          const _activeColor   = _mbMode ? '#a78bfa' : '#60A5FA';
                          const _activeBg      = _mbMode ? 'rgba(139, 92, 246, 0.15)' : 'rgba(59, 130, 246, 0.15)';
                          const _activeBorder  = _mbMode ? 'rgba(139, 92, 246, 0.4)'  : 'rgba(59, 130, 246, 0.4)';
                          const _activeBtnBg   = _mbMode ? 'rgba(139, 92, 246, 0.2)'  : 'rgba(59, 130, 246, 0.2)';
                          const _activeBtnText = _mbMode ? '#c4b5fd' : '#93C5FD';
                          const _label = disableMinBooksFilter
                            ? 'Min books filter off (all results)'
                            : _mbMode
                              ? `Showing ${_minVal}+ My Books`
                              : `Showing ${_minVal}+ sportsbooks`;
                          return (
                            <View style={{ flexDirection: 'row', marginBottom: 16 }}>
                              <View style={{
                                flex: 1,
                                backgroundColor: disableMinBooksFilter ? 'rgba(75, 85, 99, 0.15)' : _activeBg,
                                borderWidth: 1,
                                borderColor: disableMinBooksFilter ? 'rgba(75, 85, 99, 0.4)' : _activeBorder,
                                borderRadius: 10, padding: 10,
                                flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 6,
                              }}>
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                  <Text style={{ fontSize: 13 }}>{_mbMode ? '⭐' : '📚'}</Text>
                                  <Text style={{ color: disableMinBooksFilter ? '#9CA3AF' : _activeColor, fontSize: 12, fontWeight: '500' }}>
                                    {_label}
                                  </Text>
                                </View>
                                <PressableScale
                                  onPress={() => setDisableMinBooksFilter(v => !v)}
                                  style={{
                                    backgroundColor: disableMinBooksFilter ? 'rgba(75, 85, 99, 0.25)' : _activeBtnBg,
                                    borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
                                  }}
                                >
                                  <Text style={{ color: disableMinBooksFilter ? '#D1D5DB' : _activeBtnText, fontSize: 11, fontWeight: '600' }}>
                                    {disableMinBooksFilter ? 'Restore' : 'Show All'}
                                  </Text>
                                </PressableScale>
                              </View>
                            </View>
                          );
                        })()}

                        {/* Banner shown when min-books filter was auto-relaxed from a below-threshold background query */}
                        {_relaxMinBooksFromBg && disableMinBooksFilter && fetchedOptions.length > 0 && (
                          <View style={{
                            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            borderWidth: 1, borderColor: 'rgba(245, 158, 11, 0.3)',
                            borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
                            marginBottom: 12, gap: 8,
                          }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
                              <Text style={{ fontSize: 13 }}>📚</Text>
                              <Text style={{ color: 'rgba(251, 191, 36, 0.9)', fontSize: 12, fontWeight: '500', flexShrink: 1 }}>
                                {isSgpResults
                                  ? 'Min books relaxed — most SGPs here only have DraftKings + FanDuel'
                                  : `Showing lines with fewer than ${parseInt(userProfile?.preferences?.minBooksStraights, 10) || 5} books — none of this search's lines met your minimum`}
                              </Text>
                            </View>
                            <PressableScale
                              onPress={() => { setDisableMinBooksFilter(false); setRelaxMinBooksFromBg(false); }}
                              style={{
                                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
                              }}
                            >
                              <Text style={{ color: '#FCD34D', fontSize: 11, fontWeight: '600' }}>Restore</Text>
                            </PressableScale>
                          </View>
                        )}

                        {/* ⭐ N3: Broadened-results banner — the backend substituted intent
                            (related market, LLM reinterpretation, nearby lines, matchup
                            fallback…). Always disclosed, never silent. "Exact only" re-runs
                            with noAutoBroaden for exact matches or an honest empty state. */}
                        {autoBroadened && fetchedOptions.length > 0 && (
                          <View style={{
                            flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                            borderWidth: 1, borderColor: 'rgba(245, 158, 11, 0.3)',
                            borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
                            marginBottom: 12, gap: 8,
                          }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flex: 1 }}>
                              <Ionicons name="swap-horizontal-outline" size={14} color="rgba(251, 191, 36, 0.9)" />
                              <Text style={{ color: 'rgba(251, 191, 36, 0.9)', fontSize: 12, fontWeight: '500', flexShrink: 1 }}>
                                {autoBroadened.note || 'Showing broadened results — not an exact match for your query.'}
                              </Text>
                            </View>
                            <PressableScale
                              onPress={undoAutoBroaden}
                              style={{
                                backgroundColor: 'rgba(245, 158, 11, 0.2)',
                                borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3,
                              }}
                            >
                              <Text style={{ color: '#FCD34D', fontSize: 11, fontWeight: '600' }}>Exact only</Text>
                            </PressableScale>
                          </View>
                        )}

                        {/* The requested book priced none of these SGPs (backend sets
                            _bookUnavailable + _message). Disclose it instead of silently
                            showing other books. */}
                        {responseMetadata?._bookUnavailable && fetchedOptions.length > 0 && (
                          <View
                            testID="sgp-book-unavailable-banner"
                            style={{
                              flexDirection: 'row', alignItems: 'center', gap: 6,
                              backgroundColor: 'rgba(59, 130, 246, 0.1)',
                              borderWidth: 1, borderColor: 'rgba(59, 130, 246, 0.3)',
                              borderRadius: 10, paddingHorizontal: 12, paddingVertical: 8,
                              marginBottom: 12,
                            }}
                          >
                            <Ionicons name="information-circle-outline" size={14} color="rgba(147, 197, 253, 0.95)" />
                            <Text style={{ color: 'rgba(147, 197, 253, 0.95)', fontSize: 12, fontWeight: '500', flexShrink: 1 }}>
                              {responseMetadata._message || `No ${responseMetadata._bookUnavailable} SGPs are available, so here are the top SGPs from other books.`}
                            </Text>
                          </View>
                        )}

                        {fetchedOptions.length ? (
                          <View style={styles.betSlipSection}>
                            {!shouldUseNewResultsUI && (<>
                            <View style={styles.betSlipHeader} nativeID="suggested-legs-anchor">
                              <View style={styles.betSlipTitleContainer}>
                                <View style={styles.betSlipTitle}>
                                  <Text style={styles.betSlipTitleText}>Suggested Legs</Text>
                                    <Text style={styles.betSlipSubtitle}>
                                    {pendingQuery ? `Query: ${pendingQuery}` : 'Select up to 3 legs'} (Saved {selectedLegs.length}/{MAX_LEGS}){didBroaden ? ' • broadened' : ''}
                                    </Text>
                                </View>
                                {/* Feedback buttons */}
                                <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginLeft: 12 }}>
                                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>How did AI do?</Text>
                                  <PressableScale 
                                    onPress={() => handleQueryFeedback('up')}
                                    style={[styles.feedbackButton, queryFeedback === 'up' && styles.feedbackButtonActive]}
                                  >
                                    <Text style={{ fontSize: 16 }}>{queryFeedback === 'up' ? '👍' : '👍🏻'}</Text>
                                  </PressableScale>
                                  <PressableScale 
                                    onPress={() => handleQueryFeedback('down')}
                                    style={[styles.feedbackButton, queryFeedback === 'down' && styles.feedbackButtonActive]}
                                  >
                                    <Text style={{ fontSize: 16 }}>{queryFeedback === 'down' ? '👎' : '👎🏻'}</Text>
                                  </PressableScale>
                                </View>
                            </View>
                            <GradientButton
                                text={`Save BetSlip (${selectedLegs.length}/${computeCap()})`}
                                onPress={handleSaveBetSlip}
                                style={styles.viewBetslipButton}
                                gradientColors={["#707070", "#FFFFFF"]}
                            />
                        </View>
                        </>)}
                        <View style={styles.betCardsContainer}>
                              <SgpResultsRenderer
                                revealActive={revealActive}
                                cardReferenceTime={cardReferenceTime}
                                _allBooksCount={_allBooksCount}
                                _effectiveBooksSort={_effectiveBooksSort}
                                _getMyBooksBest={_getMyBooksBest}
                                _myBooksCount={_myBooksCount}
                                _myBooksList={_myBooksList}
                                _sortEvForBook={_sortEvForBook}
                                _wantsMyBooksMode={_wantsMyBooksMode}
                                availableMarkets={availableMarkets}
                                availablePlayers={availablePlayers}
                                availableSportsbooks={availableSportsbooks}
                                availableLegCounts={availableLegCounts}
                                canShowDevResultsCopy={canShowDevResultsCopy}
                                clearAllFilters={clearAllFilters}
                                computeCap={computeCap}
                                consensusOptions={consensusOptions}
                                copyResultsFlash={copyResultsFlash}
                                currentPage={currentPage}
                                debugPanelExpanded={debugPanelExpanded}
                                didBroaden={didBroaden}
                                disableMyBooksPriority={disableMyBooksPriority}
                                fetchedOptions={fetchedOptions}
                                filterDeepLinksOnly={filterDeepLinksOnly}
                                filterLegCount={filterLegCount}
                                filterLineType={filterLineType}
                                filterMarkets={filterMarkets}
                                filterPlayers={filterPlayers}
                                filterPositiveEvOnly={filterPositiveEvOnly}
                                filterResultType={filterResultType}
                                filterSportsbooks={filterSportsbooks}
                                filteredAndSortedOptions={filteredAndSortedOptions}
                                getFixtureLogosFromTicker={getFixtureLogosFromTicker}
                                getMarketPeers={getMarketPeers}
                                handleAddFetchedLeg={handleAddFetchedLeg}
                                handleCopyAllResults={handleCopyAllResults}
                                handleQueryFeedback={handleQueryFeedback}
                                handleSaveBetSlip={handleSaveBetSlip}
                                handleUnwatchFixture={handleUnwatchFixture}
                                handleWatchFixture={handleWatchFixture}
                                isLiveQuery={isLiveQuery}
                                itemsPerPage={itemsPerPage}
                                openDeepLink={openDeepLink}
                                pendingQuery={pendingQuery}
                                preferredSportsbook={preferredSportsbook}
                                queryFeedback={queryFeedback}
                                requestedDirection={requestedDirection}
                                requestedLine={requestedLine}
                                responseMetadata={responseMetadata}
                                resultsListForCopyRef={resultsListForCopyRef}
                                searchText={searchText}
                                selectedLegs={selectedLegs}
                                setBooksViewOverride={setBooksViewOverride}
                                setCurrentPage={setCurrentPage}
                                setDebugPanelExpanded={setDebugPanelExpanded}
                                setDidBroaden={setDidBroaden}
                                setFilterDeepLinksOnly={setFilterDeepLinksOnly}
                                setFilterLegCount={setFilterLegCount}
                                setFilterLineType={setFilterLineType}
                                setFilterMarkets={setFilterMarkets}
                                setFilterPlayers={setFilterPlayers}
                                setFilterPositiveEvOnly={setFilterPositiveEvOnly}
                                setFilterResultType={setFilterResultType}
                                setFilterSportsbooks={setFilterSportsbooks}
                                setPreferredSportsbook={setPreferredSportsbook}
                                setSearchText={setSearchText}
                                setShowAllMathOverride={setShowAllMathOverride}
                                setSortBy={setSortBy}
                                sgpGenerationStats={sgpGenerationStats}
                                shouldUseNewResultsUI={shouldUseNewResultsUI}
                                showAllMathOverride={showAllMathOverride}
                                sortBy={sortBy}
                                userProfile={userProfile}
                                watchedFixtures={watchedFixtures}
                              />
                            </View>
                            {!shouldUseNewResultsUI && (
                            <View style={{ alignSelf: 'stretch', marginTop: 8, flexDirection: 'row', gap: 12, justifyContent: 'flex-end', alignItems: 'center' }}>
                              {noResults ? (
                                <View style={{ flexDirection: 'row', gap: 12 }}>
                                  <GradientButton text="Broaden Search" onPress={requestBroaden} style={styles.footerButton} />
                                </View>
                              ) : (
                                <GradientButton text={`Review BetSlip (${selectedLegs.length})`} onPress={() => setShowBetSlipModal(true)} style={styles.footerButton} />
                              )}
                            </View>
                            )}

                            {/* Sticky Builder Bar */}
                            {/* sticky footer moved outside */}
                          </View>
                        ) : clarification ? (
                          // ⭐ N2: Clarify-or-abstain — the backend found multiple
                          // candidate teams (e.g. "rangers" = NHL or MLB) and asked
                          // instead of guessing. Each chip re-fires the search with
                          // the full team name substituted into the original prompt.
                          <View style={styles.noResultsContainer}>
                            <View style={styles.noResultsIconWrap}>
                              <Ionicons name="help-circle-outline" size={36} color="rgba(167,139,250,0.6)" />
                            </View>
                            <Text style={styles.noResultsTitle}>
                              {clarification.question || 'Which team did you mean?'}
                            </Text>
                            <Text style={styles.noResultsSubtitle}>
                              {`"${clarification.term || pendingQuery}" matches more than one team — pick one to continue.`}
                            </Text>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 10, justifyContent: 'center', marginTop: 18 }}>
                              {(clarification.options || []).map((opt) => (
                                <PressableScale
                                  key={opt.label || opt.team}
                                  onPress={() => resolveClarification(opt)}
                                  style={{
                                    flexDirection: 'row', alignItems: 'center', gap: 6,
                                    paddingVertical: 10, paddingHorizontal: 16,
                                    borderRadius: 12, borderWidth: 1,
                                    borderColor: 'rgba(167,139,250,0.45)',
                                    backgroundColor: 'rgba(139,92,246,0.12)',
                                  }}
                                >
                                  <Text style={{ color: '#c4b5fd', fontSize: 14, fontWeight: '600' }}>
                                    {opt.label || opt.team}
                                  </Text>
                                </PressableScale>
                              ))}
                            </View>
                            <PressableScale
                              style={[styles.noResultsBtn, styles.noResultsBtnSecondary, { marginTop: 20 }]}
                              onPress={goBack}
                            >
                              <Ionicons name="arrow-back-outline" size={15} color="rgba(255,255,255,0.7)" style={{ marginRight: 6 }} />
                              <Text style={[styles.noResultsBtnText, { color: 'rgba(255,255,255,0.7)' }]}>New Search</Text>
                            </PressableScale>
                          </View>
                        ) : hasStartedSearch && !loading ? (
                          // No results empty state — only after the search has finished
                          <View style={styles.noResultsContainer}>
                            {/* Icon */}
                            <View style={styles.noResultsIconWrap}>
                              <Ionicons
                                name={fixturesFoundNoOdds ? 'calendar-outline' : 'search-outline'}
                                size={36}
                                color="rgba(255,255,255,0.25)"
                              />
                            </View>

                            {/* Heading + subtext */}
                            <Text style={styles.noResultsTitle}>
                              {fixturesFoundNoOdds
                                ? `Found ${fixturesFoundNoOdds.fixtureCount} game${fixturesFoundNoOdds.fixtureCount !== 1 ? 's' : ''} — no odds yet`
                                : (searchEmpty?.title || 'No odds found')}
                            </Text>
                            <Text style={styles.noResultsSubtitle}>
                              {fixturesFoundNoOdds
                                ? `We located the ${fixturesFoundNoOdds.teamName || 'fixture'} but odds haven't been posted. Check back closer to game time.`
                                : searchEmpty?.body
                                  ? searchEmpty.body
                                  : responseMetadata?._resultValidation?.understood
                                    ? `${responseMetadata._resultValidation.understood}, but no lines are available right now.${responseMetadata._resultValidation.suggestion ? ` ${responseMetadata._resultValidation.suggestion}` : ''}`
                                    : "We couldn't find odds matching this query. Try broadening the search or tweaking your wording."}
                            </Text>

                            {/* Action buttons */}
                            <View style={styles.noResultsActions}>
                              {fixturesFoundNoOdds ? (
                                <PressableScale
                                  style={[styles.noResultsBtn, styles.noResultsBtnPrimary]}
                                  onPress={() => handleSaveQuery(pendingQuery)}
                                >
                                  <Ionicons name="bookmark-outline" size={15} color="#fff" style={{ marginRight: 6 }} />
                                  <Text style={styles.noResultsBtnText}>Save Query</Text>
                                </PressableScale>
                              ) : searchEmpty?.kind === 'freezeSnapshotMiss' ? null : (
                                <PressableScale
                                  style={[styles.noResultsBtn, styles.noResultsBtnPrimary]}
                                  onPress={requestBroaden}
                                >
                                  <Ionicons name="expand-outline" size={15} color="#fff" style={{ marginRight: 6 }} />
                                  <Text style={styles.noResultsBtnText}>Broaden Search</Text>
                                </PressableScale>
                              )}
                              <PressableScale
                                style={[styles.noResultsBtn, styles.noResultsBtnSecondary]}
                                onPress={goBack}
                              >
                                <Ionicons name="arrow-back-outline" size={15} color="rgba(255,255,255,0.7)" style={{ marginRight: 6 }} />
                                <Text style={[styles.noResultsBtnText, { color: 'rgba(255,255,255,0.7)' }]}>New Search</Text>
                              </PressableScale>
                            </View>

                            {/* Help / Docs link */}
                            <PressableScale
                              onPress={() => navigateTo('Docs')}
                              style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 20, paddingVertical: 8, paddingHorizontal: 14, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(167,139,250,0.3)', backgroundColor: 'rgba(167,139,250,0.07)' }}
                            >
                              <Ionicons name="information-circle-outline" size={16} color="#a78bfa" />
                              <Text style={{ color: '#a78bfa', fontSize: 13, fontWeight: '600' }}>How to search — query guide</Text>
                            </PressableScale>

                          </View>
                        ) : null}

                    </BlurView>
                </Animated.View>
            </View>

            {/* Sticky footer (fixed to viewport bottom) */}
            {selectedLegs.length > 0 && !loading ? (
              <View style={{ position: 'fixed', bottom: 0, left: 0, right: 0, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.12)', paddingTop: 8, paddingBottom: 10, paddingHorizontal: 16, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#000', zIndex: 50 }}>
                <View style={{ flex: 1, flexDirection: 'row', gap: 16, alignItems: 'center' }}>
                  <Text style={{ color: 'rgba(255,255,255,0.9)', fontWeight: '600' }}>Selected: {selectedLegs.length} / {computeCap()}</Text>
                  {(() => {
                    const math = calculateSgpMath(selectedLegs, builderStake, betMode);
                    if (!math || selectedLegs.length < 2) return null;
                    return (
                      <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
                        {math.combinedAmerican !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            {math.betType}: <Text style={{ fontWeight: '700' }}>{math.combinedAmerican > 0 ? '+' : ''}{math.combinedAmerican}</Text>
                            {math.betType === 'SGPx' && (
                              <Text style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
                                {' '}({math.sgpLegs}SGP+{math.straightLegs}Straight)
                              </Text>
                            )}
                          </Text>
                        ) : null}
                        {math.payout !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            Payout: <Text style={{ fontWeight: '700', color: '#4ade80' }}>${math.payout.toFixed(2)}</Text>
                          </Text>
                        ) : null}
                        {math.evPercent !== null ? (
                          <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
                            EV: <Text style={{ fontWeight: '700', color: math.evPercent >= 0 ? '#4ade80' : '#f87171' }}>{math.evPercent >= 0 ? '+' : ''}{math.evPercent.toFixed(2)}%</Text>
                            {/* v1.18 P1 — user-built parlays are priced with the independence
                                model (no market R-factor), unlike cached SGPs. Label it so this
                                EV can't be mistaken for a market-correlated edge. */}
                            {math.correlationModel === 'independent' ? (
                              <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 11 }}> (independent model)</Text>
                            ) : null}
                          </Text>
                        ) : null}
                      </View>
                    );
                  })()}
                </View>
                <View style={{ flexDirection: 'row', gap: 12 }}>
                  <GradientButton text="Review BetSlip" onPress={() => setShowBetSlipModal(true)} style={[styles.footerButton, { flex: 1 }]} />
                  <GradientButton text="Share" onPress={handleShareBetSlip} style={[styles.footerButton, { flex: 1, backgroundColor: 'rgba(139, 92, 246, 0.15)', borderColor: '#8b5cf6' }]} />
                </View>
              </View>
            ) : null}
            
            <BetSlipModal
                visible={showBetSlipModal}
                onClose={() => setShowBetSlipModal(false)}
                onSubmit={handleBetSlipSubmission}
                selectedOptions={selectedOptions}
                selectedLegs={selectedLegs}
                stake={builderStake}
            />
            
            <QueryLimitModal
                visible={showQueryLimitModal}
                onClose={() => setShowQueryLimitModal(false)}
                planType={queryUsage.planType}
                queriesUsed={queryUsage.queriesUsed}
                queryLimit={queryUsage.queryLimit}
            />
            
            {showSubmissionScreen && (
                <BetSlipSubmissionScreen
                    onComplete={handleSubmissionComplete}
                    onSave={handleSaveBetSlip}
                    selectedLegs={selectedLegs}
                />
            )}
            
            <SavedRequestsModal
                visible={showSavedRequests}
                onClose={() => setShowSavedRequests(false)}
                onApplyRequest={handleApplySavedRequest}
                onSaveRequest={handleSaveRequest}
                userProfile={userProfile}
                currentQuery={pendingQuery}
            />
            
            {/* Share Modal */}
            <ShareBetSlipModal
                visible={showShareModal}
                shareUrl={shareUrl}
                onCopy={handleCopyShareUrl}
                onClose={() => setShowShareModal(false)}
            />
        </View>
    );
}
